import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { PowerProfileEditor } from "@/components/match/PowerProfileEditor";
import { isReadonlyTeamProfile } from "./team-profile-presets";
import { profileToDraft } from "./team-profile-draft";
import type { TeamProfileDraft } from "./team-profile-draft";
import type { TeamProfileCoreFragment } from "@/graphql/generated/operations";

function signature(draft: TeamProfileDraft): string {
  return JSON.stringify([draft.german, draft.english, draft.elementWeights]);
}

interface TeamProfileCardProps {
  profile: TeamProfileCoreFragment;
  busy: boolean;
  /** Would these names collide with another profile? Excludes this one. */
  isDuplicateName: (german: string, english: string) => boolean;
  onSave: (draft: TeamProfileDraft) => void;
  onDelete: () => void;
  /** Weiter zum Anlegeformular, vorbelegt mit diesem Profil als Quelle. */
  onDuplicate: () => void;
}

// One Power Profile: name (both locales) and weights. The three company
// defaults lock name and weights and hide delete — see team-profile-presets.ts
// for why that guard is keyed on id rather than name.
export function TeamProfileCard({
  profile,
  busy,
  isDuplicateName,
  onSave,
  onDelete,
  onDuplicate,
}: TeamProfileCardProps) {
  const intl = useIntl();
  const readOnly = isReadonlyTeamProfile(profile.id);

  const serverDraft = useMemo(() => profileToDraft(profile), [profile]);
  const serverSignature = signature(serverDraft);
  const [draft, setDraft] = useState(serverDraft);
  const [syncedFrom, setSyncedFrom] = useState(serverSignature);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Re-sync only when the persisted name/weights actually changed. Adjusted
  // during render rather than in an effect — the documented pattern for state
  // derived from props, and it avoids a render pass showing the stale draft.
  if (serverSignature !== syncedFrom) {
    setDraft(serverDraft);
    setSyncedFrom(serverSignature);
  }

  const dirty = signature(draft) !== syncedFrom;
  const label = profile.localizedName?.localized || profile.name || profile.id;
  const nameMissing = draft.german.trim().length === 0 || draft.english.trim().length === 0;
  const nameTaken = isDuplicateName(draft.german.trim(), draft.english.trim());

  return (
    <>
      {/* Detailspalte des Master-Detail-Layouts: das gewaehlte Profil ist immer
          vollstaendig sichtbar, daher kein Disclosure mehr. Die Auswahl trifft
          die Liste links (AdminMasterDetail). */}
      <div id="teamprofile-card" className="box bg-surface p-5">
        {/* Kein Vorlagen-Badge hier: die Liste links traegt ihn bereits, und
            der Hinweistext darunter sagt dasselbe vollstaendiger. Zwei Marken
            fuer eine Aussage auf einem Bildschirm sind Redundanz. */}
        <div className="mb-5 flex items-center justify-between gap-3">
          <h3 id="teamprofile-card-name" className="text-smycles-body font-medium">{label}</h3>
          {/* Duplizieren gilt auch fuer die gesperrten Vorlagen, deshalb im
              Kopf statt in der Aktionszeile, die bei readOnly fehlt. */}
          <Button
            id="teamprofile-card-duplicate"
            variant="chip"
            size="sm"
            disabled={busy}
            onClick={onDuplicate}
          >
            {intl.formatMessage({ id: "teamProfile.duplicateAction", defaultMessage: "Duplicate" })}
          </Button>
        </div>
        <div className="space-y-6">
          {readOnly ? (
            // Gedaempfte Flaeche aus den bestehenden Utilities (.box-flat +
            // bg-ink/[0.02], die leiseste etablierte Toenung): der Hinweis soll
            // als eigener Block lesbar sein, ohne wie ein Fehler oder ein
            // Handlungsaufruf zu wirken. Bewusst KEINE neue .smycles-*-Klasse,
            // solange es genau eine Stelle ist.
            <div
              id="teamprofile-presethint"
              className="box-flat bg-ink/[0.02] px-3 py-2 text-smycles-meta text-ink-muted"
            >
              {intl.formatMessage({
                id: "teamProfile.presetHint",
                defaultMessage:
                  "Name and weights of this template are set by Smycles and cannot be changed. Create your own profile if you need different weights.",
              })}
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-3 @sm:grid-cols-2">
            <label className="block">
              <span className="text-smycles-meta text-ink-muted">
                {intl.formatMessage({ id: "teamProfile.nameDe", defaultMessage: "Name (German)" })}
              </span>
              <input
                id="teamprofile-namede"
                value={draft.german}
                disabled={readOnly || busy}
                onChange={(e) => setDraft({ ...draft, german: e.target.value })}
                className="bg-surface mt-1 w-full rounded-md px-3 py-2 text-smycles-body ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui disabled:opacity-60"
              />
            </label>
            <label className="block">
              <span className="text-smycles-meta text-ink-muted">
                {intl.formatMessage({ id: "teamProfile.nameEn", defaultMessage: "Name (English)" })}
              </span>
              <input
                id="teamprofile-nameen"
                value={draft.english}
                disabled={readOnly || busy}
                onChange={(e) => setDraft({ ...draft, english: e.target.value })}
                className="bg-surface mt-1 w-full rounded-md px-3 py-2 text-smycles-body ring-1 ring-ink/10 focus:outline-none focus:ring-ink-ui disabled:opacity-60"
              />
            </label>
          </div>

          <div>
            <div className="text-smycles-meta text-ink-muted">
              {intl.formatMessage({ id: "teamProfile.weights", defaultMessage: "Importance" })}
            </div>
            {/* Die Kachelgroesse steuert die Konvention ueber die Spaltenzahl im
                Container, nicht ueber die Tile selbst (UI_CONVENTIONS, "Power /
                Element Tiles"). Der Editor rechnet per Container-Query
                (@sm/@lg), also bestimmt die Containerbreite die Kantenlaenge.
                Das Settings-Panel ist deutlich breiter als der Match-Brief, in
                dem der Editor entworfen wurde — ohne Deckel werden dieselben
                Kacheln hier fast doppelt so gross. Begrenzt statt die Tile
                anzufassen, damit beide Einbettungen gleich aussehen. */}
            <div className="mt-2 max-w-2xl">
              <PowerProfileEditor
                readOnly={readOnly}
                profile={{ id: profile.id, name: label, elementWeights: draft.elementWeights }}
                onChange={(next) =>
                  setDraft({ ...draft, elementWeights: next?.elementWeights ?? draft.elementWeights })
                }
              />
            </div>
          </div>

          {!readOnly && nameTaken ? (
            <div id="teamprofile-duplicate" className="text-smycles-meta text-ink-muted">
              {intl.formatMessage({
                id: "teamProfile.duplicate",
                defaultMessage: "A profile with this name already exists.",
              })}
            </div>
          ) : null}

          {!readOnly ? (
            <div className="flex items-center gap-2">
              <Button
                id="teamprofile-discard"
                variant="secondary"
                size="compact"
                disabled={busy || !dirty}
                onClick={() => setDraft(serverDraft)}
              >
                {intl.formatMessage({ id: "teamProfile.reset", defaultMessage: "Discard changes" })}
              </Button>
              <Button
                id="teamprofile-save"
                variant="primary"
                size="compact"
                disabled={busy || !dirty || nameMissing || nameTaken}
                onClick={() => onSave(draft)}
              >
                {intl.formatMessage({ id: "teamProfile.save", defaultMessage: "Save" })}
              </Button>
              <Button
                id="teamprofile-delete"
                variant="ghost"
                size="compact"
                className="ml-auto"
                disabled={busy}
                onClick={() => setConfirmingDelete(true)}
              >
                {intl.formatMessage({ id: "teamProfile.delete", defaultMessage: "Delete profile" })}
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <ConfirmDialog
        id="teamprofile-deletedialog"
        open={confirmingDelete}
        title={intl.formatMessage(
          { id: "teamProfile.confirmDelete.title", defaultMessage: "Delete “{label}”?" },
          { label },
        )}
        message={intl.formatMessage({
          id: "teamProfile.confirmDelete.message",
          defaultMessage:
            "The profile is removed for good and it will no longer be offered when designing a Match.",
        })}
        confirmLabel={intl.formatMessage({ id: "teamProfile.delete", defaultMessage: "Delete profile" })}
        onConfirm={() => {
          onDelete();
          setConfirmingDelete(false);
        }}
        onCancel={() => setConfirmingDelete(false)}
      />
    </>
  );
}
